import {
  Alert,
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Paper,
  Stack,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import {
  IconAlertCircle,
  IconFilter,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import type { ChangeEvent } from "react";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useShippedCases } from "../../hooks/useWorkflows";
import type {
  ShippedCase,
  ShippedCasesFilters,
  ShippingFilterOption,
} from "../../services/workflows.service";
import ShippedCaseTableRow from "./ShippedCaseTableRow";
import ShippedCasesFilterModal from "./ShippedCasesFilterModal";
import type { ShippedCaseFilterValues } from "./ShippedCasesFilterModal";
import ShippingTrackingModal from "./ShippingTrackingModal";

const PAGE_SIZE = 20;
const DEFAULT_CASE_TYPES: ShippingFilterOption[] = [
  { value: "remote", label: "Remote" },
  { value: "tc_return", label: "TC Return" },
];

const createDefaultFilters = (): ShippedCaseFilterValues => ({
  caseType: null,
  pathologists: [],
  sendingPractices: [],
  dateShippedFrom: null,
  dateShippedTo: null,
});

export default function ShippedCasesPanel() {
  const [search, setSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [page, setPage] = useState(1);
  const [filters, setFilters] =
    useState<ShippedCaseFilterValues>(createDefaultFilters);
  const [selectedCase, setSelectedCase] = useState<ShippedCase | null>(null);
  const [filtersOpened, { open: openFilters, close: closeFilters }] =
    useDisclosure(false);
  const canManage = useHasPermission("shipping.manage");

  const queryFilters = useMemo<ShippedCasesFilters>(
    () => ({
      page,
      pageSize: PAGE_SIZE,
      search: debouncedSearch.trim() || undefined,
      caseType: filters.caseType ?? undefined,
      pathologists:
        filters.pathologists.length > 0 ? filters.pathologists : undefined,
      sendingPractices:
        filters.sendingPractices.length > 0
          ? filters.sendingPractices
          : undefined,
      dateShippedFrom: filters.dateShippedFrom ?? undefined,
      dateShippedTo: filters.dateShippedTo ?? undefined,
    }),
    [debouncedSearch, filters, page]
  );
  const shippedQuery = useShippedCases(queryFilters);
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((shippedQuery.data?.total ?? 0) / PAGE_SIZE)),
    [shippedQuery.data?.total]
  );
  const activeFilterCount = useMemo(
    () =>
      Number(filters.caseType !== null) +
      filters.pathologists.length +
      filters.sendingPractices.length +
      Number(filters.dateShippedFrom !== null) +
      Number(filters.dateShippedTo !== null),
    [filters]
  );
  const facets = useMemo(
    () =>
      shippedQuery.data?.facets ?? {
        caseTypes: DEFAULT_CASE_TYPES,
        pathologists: [],
        sendingPractices: [],
      },
    [shippedQuery.data?.facets]
  );

  const handleSearchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSearch(event.currentTarget.value);
      setPage(1);
    },
    []
  );

  const handlePageChange = useCallback((nextPage: number) => {
    setPage(nextPage);
  }, []);

  const handleApplyFilters = useCallback(
    (nextFilters: ShippedCaseFilterValues) => {
      setFilters(nextFilters);
      setPage(1);
      closeFilters();
    },
    [closeFilters]
  );

  const handleClearAppliedFilters = useCallback(() => {
    setFilters(createDefaultFilters());
    setPage(1);
  }, []);

  const handleOpenTracking = useCallback((shippedCase: ShippedCase) => {
    setSelectedCase(shippedCase);
  }, []);

  const handleCloseTracking = useCallback(() => {
    setSelectedCase(null);
  }, []);

  const handleRefresh = useCallback(() => {
    shippedQuery.refetch();
  }, [shippedQuery]);

  return (
    <Stack gap="md">
      <Group align="stretch" grow>
        <Paper p="md" withBorder style={{ flex: 2 }}>
          <Stack gap="sm">
            <Group>
              <IconSearch size={20} />
              <Text fw={500}>Search</Text>
            </Group>
            <TextInput
              placeholder="Search by case number or patient name..."
              value={search}
              onChange={handleSearchChange}
              leftSection={<IconSearch size={16} />}
            />
          </Stack>
        </Paper>
        <Paper p="md" withBorder style={{ flex: 1 }}>
          <Stack gap="sm">
            <Group justify="space-between">
              <Group>
                <IconFilter size={20} />
                <Text fw={500}>Filters</Text>
              </Group>
              <Button
                variant="subtle"
                size="compact-sm"
                leftSection={<IconRefresh size={14} />}
                onClick={handleRefresh}
                loading={shippedQuery.isFetching}
              >
                Refresh
              </Button>
            </Group>
            <Button variant="light" onClick={openFilters} fullWidth>
              {activeFilterCount > 0
                ? `Edit Filters (${activeFilterCount})`
                : "Add Filters"}
            </Button>
            {activeFilterCount > 0 && (
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={handleClearAppliedFilters}
              >
                Clear All
              </Button>
            )}
            <Text size="sm" c="dimmed">
              {shippedQuery.data?.total ?? 0} shipped case(s)
            </Text>
          </Stack>
        </Paper>
      </Group>

      <Paper p="md" withBorder>
        <Stack gap="md">
          <Group justify="space-between">
            <Text fw={500} size="lg">
              Shipped cases ({shippedQuery.data?.total ?? 0})
            </Text>
            {totalPages > 1 && (
              <Pagination
                total={totalPages}
                value={page}
                onChange={handlePageChange}
                size="sm"
              />
            )}
          </Group>

          {shippedQuery.isLoading ? (
            <Center p="xl">
              <Loader />
            </Center>
          ) : shippedQuery.isError ? (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="Shipped cases unavailable"
              color="red"
              role="alert"
            >
              Please try again. If the problem continues, contact support.
            </Alert>
          ) : (shippedQuery.data?.items.length ?? 0) === 0 ? (
            <Alert
              icon={<IconAlertCircle size={16} />}
              title="No shipped cases"
            >
              No shipped cases match the current search and filters.
            </Alert>
          ) : (
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Case Number</Table.Th>
                  <Table.Th>Patient</Table.Th>
                  <Table.Th>Date shipped</Table.Th>
                  <Table.Th>Pathologist</Table.Th>
                  <Table.Th>Sending Practice</Table.Th>
                  <Table.Th>Current Status</Table.Th>
                  <Table.Th>Tracking Number</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {shippedQuery.data?.items.map((shippedCase) => (
                  <ShippedCaseTableRow
                    key={shippedCase.id}
                    shippedCase={shippedCase}
                    onOpen={handleOpenTracking}
                  />
                ))}
              </Table.Tbody>
            </Table>
          )}
        </Stack>
      </Paper>

      <ShippedCasesFilterModal
        opened={filtersOpened}
        filters={filters}
        caseTypeOptions={facets.caseTypes}
        pathologistOptions={facets.pathologists}
        sendingPracticeOptions={facets.sendingPractices}
        onApply={handleApplyFilters}
        onClose={closeFilters}
      />
      <ShippingTrackingModal
        shippedCase={selectedCase}
        canManage={canManage}
        onClose={handleCloseTracking}
      />
    </Stack>
  );
}
